import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import { ref } from 'vue';
import {
    MarketGroupToColumnWidthMapping, MarketToColumnWidthLimitMapping, MarketTypeGroupingMapping, MarketTypeToColumnWidthMapping, NewBetTypeGroupingMapping, OddsDisplayBlockTypeToColumnWidthMapping, getColumnFirstMarketGroup, getColumnMarketGroups, getColumnMarketType, getDisplayMarketTypeInfos, getMainMarketGroups, getNewBetTypeDisplayInfos, isSingleMode, toBetBuilderMarketTypeInfos,
} from '@/components/oddsDisplay/marketTypeDisplayRule';
import { getMoreMarketCount } from '@/components/oddsDisplay/matchDisplay/useMatchDisplay';
import { getIsOpenMoreMarket } from '@/components/oddsDisplay/matchDisplay/useOpenMoreMarket';
import { getMarketRule } from '@/components/oddsDisplay/moreMarket/betBuilderMarketRules';
import { padRight, rotate2dArray } from '@/core/lib/array';
import { getSelectionPointForOddsDisplay } from '@/core/lib/betBuilderHelper';
import { sortBetBuilderMarketsByDisplayPriority, sortBetBuilderMarketsByPointOrder } from '@/core/lib/betBuilderPointSort';
import { betBuilderToMarketType } from '@/core/lib/betBuilderTypeConverter';
import { getMarketGroupId, getOddsPoint, getTag, is1X2, isMainMarket, isNextGoal, nextGoalMarketGroups, sortOdds } from '@/core/lib/oddsHelper';
import type { SizeInfo } from '@/directives/resize';
import type { IEvent } from '@/interface/IEvent';
import type { MarketColumnEntry } from '@/interface/IMarketTypeInfo';
import type { IOdds } from '@/interface/IOdds';
import type { IOutrightLeague } from '@/interface/IOutrightLeague';
import type { IBetBuilderMarket } from '@/interface/betBuilder';
import {
    BetBuilderMarket, BetBuilderSelectionStatus, BetBuilderStatType, BetTypeGroup, ColumnType, type MarketType, OddsDisplayBlockType, OddsDisplayMode, SportType,
} from '@/interface/enum';

const minimumOddsDisplayWidth = 856;

export const teamColumnMinWidth = 72;
const oddsDisplayWidth = ref(minimumOddsDisplayWidth);

export const updateOddsDisplayWidth = ({ width }: SizeInfo) => {
    oddsDisplayWidth.value = width;
};

const orderBy12X = ['H', 'A', 'X'];

function getBlockColumnWidth(key: OddsDisplayBlockType, sportType: SportType, isLive: boolean) {
    if (key === OddsDisplayBlockType.Time && sportType === SportType.Cricket && isLive) {
        return 146;
    }
    return OddsDisplayBlockTypeToColumnWidthMapping[key] ?? 0;
}

function getMarketColumnWidth(entry: MarketColumnEntry) {
    const firstMarketGroup = getColumnFirstMarketGroup(entry);
    return shouldUseMarketTypeColumnWidth(firstMarketGroup)
        ? MarketTypeToColumnWidthMapping[getColumnMarketType(entry)]
        : MarketGroupToColumnWidthMapping[firstMarketGroup] ?? 0;
}

function shouldUseMarketTypeColumnWidth(marketGroup: BetTypeGroup): boolean {
    const useMarketTypeColumnWidthMarketGroups = [
        BetTypeGroup.Main,
        BetTypeGroup.Basketball_FirstHalfMoneyLine,
        BetTypeGroup.AmericanFootball_FirstHalfMoneyLine,
        BetTypeGroup.Tennis_GameHandicap,
        BetTypeGroup.TableTennis_TotalMatchPoints,
        BetTypeGroup.Badminton_TotalMatchPoints,
        BetTypeGroup.Volleyball_TotalMatchPoints,
        BetTypeGroup.Hockey_FirstPeriod,
        BetTypeGroup.Baseball_FirstInningRuns,
        BetTypeGroup.Futsal_FirstHalf,
        BetTypeGroup.Soccer_PenaltyTenKick,
    ];
    return useMarketTypeColumnWidthMarketGroups.includes(marketGroup);
}

function getMarketColumnInnerWidthLimit(entry: MarketColumnEntry): number | undefined {
    const firstMarketGroup = getColumnFirstMarketGroup(entry);
    return MarketToColumnWidthLimitMapping.get(firstMarketGroup)?.get(getColumnMarketType(entry));
}

export interface MarketColumn {
    type: ColumnType.Market;
    key: MarketType;
    width: number;
    innerWidthLimit?: number;
    marketGroup: BetTypeGroup;
}

export interface BlockColumn {
    type: ColumnType.Block;
    key: OddsDisplayBlockType;
    width: number;
    innerWidthLimit?: number;
    children?: MarketColumn[];
}

export type OddsTableColumn = MarketColumn | BlockColumn

function getBlockColumn(sportType: SportType, isLive: boolean, blockType: OddsDisplayBlockType, hasChildren = false): BlockColumn {
    return {
        type: ColumnType.Block,
        key: blockType,
        width: getBlockColumnWidth(blockType, sportType, isLive),
        children: hasChildren ? [] : undefined,
    };
}

function getMarketColumn(entry: MarketColumnEntry): MarketColumn {
    return {
        type: ColumnType.Market,
        key: getColumnMarketType(entry),
        marketGroup: getColumnFirstMarketGroup(entry),
        width: getMarketColumnWidth(entry),
        innerWidthLimit: getMarketColumnInnerWidthLimit(entry),
    };
}

export function getMarketColumns(sportType: SportType, isLive: boolean, oddsDisplayMode: OddsDisplayMode): MarketColumn[] {
    const displayMarketTypeInfos = getDisplayMarketTypeInfos(sportType, isLive, oddsDisplayMode);
    return displayMarketTypeInfos.map(marketTypeInfo => getMarketColumn(marketTypeInfo));
}

function isTeamColumn({ key }: OddsTableColumn): boolean {
    return key === OddsDisplayBlockType.Team;
}

function sumColumnsWidth(columns: OddsTableColumn[]): number {
    return columns.reduce((total, { width }) => total + width, 0);
}

function getOddsTableColumnSet(sportType: SportType, isLive: boolean, oddsDisplayMode: OddsDisplayMode) {
    const teamColumn = getBlockColumn(sportType, isLive, OddsDisplayBlockType.Team);
    const marketColumns = getMarketColumns(sportType, isLive, oddsDisplayMode);
    const columns: OddsTableColumn[] = [
        getBlockColumn(sportType, isLive, OddsDisplayBlockType.Time),
        teamColumn,
        ...marketColumns,
        getBlockColumn(sportType, isLive, OddsDisplayBlockType.More),
    ];

    return { teamColumn, marketColumns, columns };
}

/**
 * Get list of columns for odds table
 * @example
 * | Time | Team | Hdp | 1X2 |  OU | Hdp | 1X2 |  OU | More |
 * | ---- | ---- | --- | --- | --- | --- | --- | --- | ---- |
 * | 1H23 | Home | 1.5 | 0.2 | 0.8 | ... | ... | ... | .... |
 */
export function getOddsTableColumns(oddsDisplayMode: OddsDisplayMode, sportType: SportType, isLive: boolean): OddsTableColumn[] {
    const { teamColumn, marketColumns, columns } = getOddsTableColumnSet(sportType, isLive, oddsDisplayMode);

    if (isSingleMode(oddsDisplayMode)) {
        // adjust team column to use all remaining space
        const remainWidth = oddsDisplayWidth.value - sumColumnsWidth(columns);
        marketColumns.forEach((column) => {
            column.width += (remainWidth / marketColumns.length);
            column.width /= 3;
        });
    } else {
        // every column grows by its share of a quarter of the surplus, the team column absorbs the rest
        const sharedSurplus = Math.max(0, oddsDisplayWidth.value - minimumOddsDisplayWidth) / 4;
        columns.forEach((column) => {
            column.width += sharedSurplus * (column.width / minimumOddsDisplayWidth);
        });

        const otherColumnsWidth = sumColumnsWidth(columns.filter(column => !isTeamColumn(column)));
        teamColumn.width = Math.max(teamColumnMinWidth, oddsDisplayWidth.value - otherColumnsWidth);
    }

    return columns;
}

export function getOddsTableGroupedColumns(sportType: SportType, isLive: boolean, oddsDisplayMode: OddsDisplayMode): OddsTableColumn[] {
    return isSingleMode(oddsDisplayMode)
        ? getOddsTableGroupedColumnsSingle(sportType, isLive)
        : getOddsTableGroupedColumnsDouble(sportType, isLive);
}

function shouldUnderSameBlock(oddsTableColumn: OddsTableColumn, groupKey: OddsDisplayBlockType): oddsTableColumn is BlockColumn {
    return oddsTableColumn && oddsTableColumn.type === ColumnType.Block && oddsTableColumn.key === groupKey;
}

/**
 * Get list of grouped columns for odds table
 * @example
 * |   V  | Team |               Full Time               |    Half Time      → type = Block
 * |             | Hdp | HOME | AWAY | OU  | HOME | AWAY | Hdp  | ...       → type = Market
 * | ---- | ---- | --- | ---  | ---  | --- | ---  | ---  | ---- |
 * | 1H23 | Home | 1.5 | 0.2  |  0.8 | ... | ...  | ...  | .... |
 */
export function getOddsTableGroupedColumnsSingle(sportType: SportType, isLive: boolean): OddsTableColumn[] {
    const columns = getOddsTableColumns(OddsDisplayMode.Single, sportType, isLive);
    const result: OddsTableColumn[] = [];
    // eslint-disable-next-line consistent-return
    columns.forEach((column) => {
        if (column.type === ColumnType.Block) return result.push(column);
        const groupKey = isMainMarket(column.marketGroup)
            ? MarketTypeGroupingMapping.get(column.key)
            : NewBetTypeGroupingMapping.get(column.marketGroup);
        if (!groupKey) return result.push(column);

        const prev = result[result.length - 1];
        const groupColumn = shouldUnderSameBlock(prev, groupKey)
            ? prev
            : getBlockColumn(sportType, isLive, groupKey, true);
        // no children means new group
        if (groupColumn.children!.length === 0) result.push(groupColumn);
        groupColumn.children!.push(column);
        groupColumn.width += column.width * 3; // 3 columns per market
    });

    return result;
}

/**
 * Get list of grouped columns for odds table
 * @example
 * |   V  | Team |    Full Time    |    Half Time    | More |
 * |             | Hdp | 1X2 |  OU | Hdp | 1X2 |  OU |      |
 * | ---- | ---- | --- | --- | --- | --- | --- | --- | ---- |
 * | 1H23 | Home | 1.5 | 0.2 | 0.8 | ... | ... | ... | .... |
 */
export function getOddsTableGroupedColumnsDouble(sportType: SportType, isLive: boolean): OddsTableColumn[] {
    const columns = getOddsTableColumns(OddsDisplayMode.Double, sportType, isLive);
    const result: OddsTableColumn[] = [];
    // eslint-disable-next-line consistent-return
    columns.forEach((column) => {
        if (column.type === ColumnType.Block) return result.push(column);

        const groupKey = isMainMarket(column.marketGroup)
            ? MarketTypeGroupingMapping.get(column.key)
            : NewBetTypeGroupingMapping.get(column.marketGroup);
        if (!groupKey) return result.push(column);

        const prev = result[result.length - 1];
        const groupColumn = shouldUnderSameBlock(prev, groupKey)
            ? prev
            : getBlockColumn(sportType, isLive, groupKey, true);
        // no children means new group
        if (groupColumn.children!.length === 0) result.push(groupColumn);
        groupColumn.children!.push(column);
        groupColumn.width += column.width;
    });

    return result;
}

function getOutrightBlockColumn(blockType: OddsDisplayBlockType): BlockColumn {
    return {
        type: ColumnType.Block,
        key: blockType,
        width: OddsDisplayBlockTypeToColumnWidthMapping[blockType] ?? 0,
        children: undefined,
    };
}

export function getOddsTableColumnsOutright(isShowMore: boolean): OddsTableColumn[] {
    const timeColumn = getOutrightBlockColumn(OddsDisplayBlockType.Time);
    const teamColumn = getOutrightBlockColumn(OddsDisplayBlockType.Team);
    const moreColumn = getOutrightBlockColumn(OddsDisplayBlockType.More);

    const result = [
        timeColumn,
        teamColumn,
        ...(isShowMore ? [moreColumn] : []),
    ];
    // adjust team column to use all remaining space
    teamColumn.width = oddsDisplayWidth.value - result.filter(({ key }) => key !== OddsDisplayBlockType.Team).reduce((acc, cur) => acc + cur.width, 0);

    return result;
}

export function getOddsTableMainMarket(
    oddsDisplayMode: OddsDisplayMode,
    event: IEvent,
    oddsList: IOdds[],
    displayMarketGroup: BetTypeGroup,
    isOddsLoaded: boolean,
) {
    const processedOddsList = oddsList.map((odds) => {
        if (is1X2(odds.marketType)) {
            const sortedPrices = orderBy(odds.prices, x => orderBy12X.indexOf(x.option.toUpperCase()));
            return { ...odds, prices: sortedPrices };
        }
        return odds;
    });
    const sortedOdds = sortOdds(processedOddsList, event.sportType);
    const groupedOddsList = groupBy(sortedOdds, (odds) => {
        const marketGroup = getMarketGroupId(odds);
        if (isMainMarket(displayMarketGroup)) {
            return isNextGoal(marketGroup)
                ? `${nextGoalMarketGroups[0]}-${odds.marketType}`
                : `${marketGroup}-${odds.marketType}`;
        }
        return odds.marketType;
    });

    const displayMarketTypeInfos = getDisplayMarketTypeInfos(event.sportType, event.isLive, oddsDisplayMode);

    const marketGroups = displayMarketGroup === BetTypeGroup.Main ? getMainMarketGroups(displayMarketTypeInfos) : [displayMarketGroup];

    const skeletonMaxRowLength = isOddsLoaded
        ? null
        : Math.max(1, ...marketGroups.flatMap(marketGroup => Object.values(event.marketTypeCount[marketGroup] ?? {})));

    const newBetTypeColumnConfig = isMainMarket(displayMarketGroup)
        ? undefined
        : getNewBetTypeDisplayInfos(event.sportType, displayMarketGroup, event.isLive);

    const getColumnOdds = (marketGroups: BetTypeGroup[], marketType: MarketType, columnIndex: number): IOdds[] => {
        if (isMainMarket(displayMarketGroup)) {
            return marketGroups.flatMap(mg => groupedOddsList[`${mg}-${marketType}`] || []);
        }
        if (newBetTypeColumnConfig) {
            const columnConfig = newBetTypeColumnConfig[columnIndex];
            if (!columnConfig) return [];
            return groupedOddsList[getColumnMarketType(columnConfig)] || [];
        }
        return marketGroups.some(mg => isMainMarket(mg)) ? (groupedOddsList[marketType] || []) : [];
    };

    const rawPriceColumns = displayMarketTypeInfos.map((entry, columnIndex) => {
        const marketGroups = getColumnMarketGroups(entry);
        const marketType = getColumnMarketType(entry);
        const marketOdds = getColumnOdds(marketGroups, marketType, columnIndex);

        const prices = marketOdds.map(odds => odds.prices.map(price => ({
            odds,
            price,
            tag: getTag(odds, price),
            point: getOddsPoint(odds, price),
        })));

        const firstMarketGroup = getColumnFirstMarketGroup(entry);
        const block = isMainMarket(firstMarketGroup) ? MarketTypeGroupingMapping.get(marketType) : NewBetTypeGroupingMapping.get(firstMarketGroup);

        return { prices, firstMarketGroup, block, marketType };
    });

    const maxRowLength = Math.max(1, skeletonMaxRowLength ?? 0, ...rawPriceColumns.map(col => col.prices.length));

    const oddsColumns = rawPriceColumns.map(({ prices, firstMarketGroup, block, marketType }) => padRight(prices, maxRowLength, []).map(priceRow => ({
        prices: priceRow,
        marketType,
        marketGroup: firstMarketGroup,
        block,
    })));

    const rows = rotate2dArray(oddsColumns);

    return rows.map((row) => {
        const blockGroups = groupBy(row, item => item.block ?? `${item.marketGroup}-${item.marketType}`);
        return row.map(item => ({
            ...item,
            blockIndex: item.block ? blockGroups[item.block].indexOf(item) : 0,
        }));
    });
}

const availableBetBuilderSelectionStatus = [
    BetBuilderSelectionStatus.Running,
    BetBuilderSelectionStatus.Suspended,
    BetBuilderSelectionStatus.Incompatible,
];

export function getOddsTableBetBuilder(
    oddsDisplayMode: OddsDisplayMode,
    event: IEvent,
    betBuilderMarkets: IBetBuilderMarket[],
    isBetBuilderDisplayPriorityEnabled: boolean,
    normalMatchPointOrder?: Map<MarketType, number[]>,
) {
    const displayMarketTypeInfos = getDisplayMarketTypeInfos(event.sportType, event.isLive, oddsDisplayMode);
    const betBuilderDisplayMarketTypeInfos = toBetBuilderMarketTypeInfos(displayMarketTypeInfos);

    const getGroupByKey = (statTypeId: number, betBuilderMarketTypeId: number) => `${BetBuilderStatType[statTypeId]}-${BetBuilderMarket[betBuilderMarketTypeId]}`;
    const groupedMarketList = groupBy(betBuilderMarkets, market => getGroupByKey(market.matchStatType.id, market.marketType.id));
    const maxRowLength = Math.max(1, ...Object.values(groupedMarketList).map(x => x.length));

    const marketTypeColumn = betBuilderDisplayMarketTypeInfos.map((marketTypeInfo) => {
        if (!marketTypeInfo) {
            return padRight([], maxRowLength, []);
        }

        const { statType, betBuilderMarketType } = marketTypeInfo;
        const key = getGroupByKey(statType, betBuilderMarketType);
        let markets: IBetBuilderMarket[] = [];

        if (isBetBuilderDisplayPriorityEnabled) {
            markets = sortBetBuilderMarketsByDisplayPriority(groupedMarketList[key] || []);
        } else {
            const mappedMarketType = betBuilderToMarketType(betBuilderMarketType, statType);
            const pointOrder = mappedMarketType ? normalMatchPointOrder?.get(mappedMarketType) : undefined;
            markets = sortBetBuilderMarketsByPointOrder(groupedMarketList[key] || [], pointOrder, mappedMarketType);
        }

        const prices = markets.map((market) => {
            const marketRule = getMarketRule(market);
            return marketRule.selectionFormatter(market.selections)
                .map(selection => ({
                    market,
                    selection,
                    point: getSelectionPointForOddsDisplay(market, selection.type.id),
                    isAvailable: 'id' in selection && availableBetBuilderSelectionStatus.includes(selection.status),
                }));
        });
        return padRight(prices, maxRowLength, []);
    });

    const rows = rotate2dArray(marketTypeColumn);
    return rows.map(row => row.map((selections, index) => {
        const marketType = getColumnMarketType(displayMarketTypeInfos[index]);
        const marketGroup = getColumnFirstMarketGroup(displayMarketTypeInfos[index]);
        // to align with getOddsTableMainMarket
        const block = isMainMarket(marketGroup) ? MarketTypeGroupingMapping.get(marketType) : NewBetTypeGroupingMapping.get(marketGroup);

        return {
            selections,
            marketType,
            marketGroup,
            block,
        };
    }));
}

export function getMatchPlaceHolderHeight(oddsDisplayMode: OddsDisplayMode, isLive: boolean, event: IEvent) {
    const marketTypeInfos = getDisplayMarketTypeInfos(event.sportType, isLive, oddsDisplayMode);
    const marketTypeList = marketTypeInfos.map(x => getColumnMarketType(x));
    const marketGroups = getMainMarketGroups(marketTypeInfos);

    let threeRowCount = 0;
    let highestOtherOddsCount = 0;

    marketGroups.forEach((marketGroup) => {
        Object.entries(event.marketTypeCount[marketGroup] ?? [])
            .forEach(([_marketType, count]) => {
                const marketType = parseInt(_marketType);
                if (!marketTypeList.includes(marketType)) return;

                const _is1X2 = is1X2(marketType);
                if (_is1X2 && count > threeRowCount) {
                    threeRowCount = count;
                }

                if (!_is1X2 && count > highestOtherOddsCount) {
                    highestOtherOddsCount = count;
                }
            });
    });

    const twoRowCount = highestOtherOddsCount > threeRowCount ? highestOtherOddsCount - threeRowCount : 0;

    const threeRowHeight = 58;
    const twoRowHeight = 40;

    const placeholderHeight = threeRowCount * threeRowHeight + twoRowCount * twoRowHeight;
    return placeholderHeight;
}

const leagueTitleHeight = 20;

export function getLeaguePlaceHolderHeight(oddsDisplayMode: OddsDisplayMode, isLive: boolean, events: IEvent[]) {
    const eventsPlaceholderHeight = events.reduce((acc, event) => acc + getMatchPlaceHolderHeight(oddsDisplayMode, isLive, event), 0);
    return leagueTitleHeight + eventsPlaceholderHeight;
}

export function getOutrightMatchPlaceHolderHeight(league: IOutrightLeague) {
    const rowHeight = 22;
    const matchPlaceholderHeight = Math.ceil(league.oddsCount / 2) * rowHeight;
    return matchPlaceholderHeight;
}

export function getOutrightLeaguePlaceHolderHeight(league: IOutrightLeague) {
    return leagueTitleHeight + getOutrightMatchPlaceHolderHeight(league);
}

export function getMoreMarketPlaceHolderHeight(oddsDisplayMode: OddsDisplayMode, event: IEvent, marketGroupId: BetTypeGroup): number {
    const moreMarketCount = getMoreMarketCount(oddsDisplayMode, event, marketGroupId);
    if (moreMarketCount === 0) return 0;

    const isOpenMoreMarket = getIsOpenMoreMarket(oddsDisplayMode, event, marketGroupId);
    if (!isOpenMoreMarket) return 0;

    let estimatedMoreMarketPlaceHolderHeight = 0;
    if (moreMarketCount > 8) {
        estimatedMoreMarketPlaceHolderHeight = moreMarketCount * 40;
    } else if (moreMarketCount > 5) {
        estimatedMoreMarketPlaceHolderHeight = moreMarketCount * 55;
    } else {
        estimatedMoreMarketPlaceHolderHeight = moreMarketCount * 60;
    }

    return estimatedMoreMarketPlaceHolderHeight;
}
